import React, { FC, useMemo } from 'react';
import { Modal, Spin } from 'antd';
import styled from '@emotion/styled';
import _ from 'lodash';
import OptimizePlanTableHeader, { TableHeader } from '../OptimizePlanModal/OptimizePlanTable/OptimizePlanTableHeader';
import { FormData, IApiConstraintDetails, Level, OptimisationPlanData } from '../OptimizePlanModal/OptimizePlan';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import { sendNotification } from 'utils/helperMethods';
import OptimizeTableContent, { InputTypes } from '../OptimizePlanModal/OptimizePlanTable/OptimizeTableContent';
import OptimizePlanForm from '../OptimizePlanModal/OptimizePlanForm';
import { OptimizeOption, useOptimizeOptions } from 'hooks/tradePromoOptimization/useOptimizeOptions';
import ScopeOfOptimization from '../OptimizePlanModal/ScopeOfOptimization';
import { useWindowSize } from 'usehooks-ts';

type Props = {
    open: boolean;
    setOpenOptimizeResultModal: React.Dispatch<React.SetStateAction<boolean>>;
    optimizedPlanId: Number;
};

interface IResultApiResponse {
    base_input: BaseInput;
    constraints: IApiConstraintDetails[];
}

export type BaseInput = {
    objective: string;
    start_date: number;
    max_promo_weeks: number;
    concurrent_tprs: number;
    concurrent_fds: number;
    concurrent_mdfs: number;
    brands_list: string[];
    sub_brands_list: string[];
    optimization_attribute_level: Level;
    keep_top: number;
};

const CustomModal = styled(Modal)`
    .ant-modal-body {
        padding: 0;
    }
    .ant-modal-content {
        padding: 12px 16px;
        border-radius: 8px !important;
    }
    .ant-modal-header {
        padding: 8px 12px;

        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
        padding: 16px 0 8px 0;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 400 !important;
    }
`;

const tableHeaders: TableHeader[] = [
    { key: 'kpi', label: 'KPI', allowSort: false },
    { key: 'constraints', label: 'Constraints', allowSort: false },
    { key: 'pre_snapshot', label: 'Original', allowSort: false },
    { key: 'optimised', label: 'Optimized', allowSort: false },
    { key: 'variance', label: 'Variance', allowSort: false },
];

const OptimizeResultModal: FC<Props> = ({ open, setOpenOptimizeResultModal, optimizedPlanId }) => {
    const { optimizeOptions } = useOptimizeOptions();
    const { width, height } = useWindowSize();
    const percentageHeight = height * 0.9;
    const percentageWidth = width * 0.8;

    const tableColumns = [
        { key: 'label', type: InputTypes.Text, isEditable: false },
        {
            key: '',
            rangeInputKeys: ['min_value', 'max_value'],
            type: InputTypes.Range,
            isEditable: false,
            className: 'text-center',
        },
        { key: 'originalValue', type: InputTypes.Text, isEditable: false, className: 'text-center' },
        { key: 'optimized', type: InputTypes.Text, isEditable: false, className: 'text-center' },
        { key: 'variance', type: InputTypes.Text, isEditable: false, className: 'text-center', highlightText: true },
    ];

    const { loading, data, error } = useCommonApiHandler({
        method: 'get',
        queryKey: ['get_optimisation_result', { optimizedPlanId }],
        queryConfig: { enabled: !!optimizedPlanId && open, cacheTime: 0, staleTime: 0 },
        url: `${tradePromoOptimizationUrls.getOptimizationResult}/${optimizedPlanId}`,
        parserFunction: (res: { data: IResultApiResponse; error: any }) => {
            try {
                if (res?.error) {
                    setOpenOptimizeResultModal(false);
                    sendNotification('error', 'Failed to load optimization results');
                    return { error: true };
                }
                const { base_input, constraints } = res.data;
                const level = base_input.optimization_attribute_level
                    ? base_input.optimization_attribute_level
                    : 'category';
                const tableData = getParsedTableData(level, constraints);
                const formData: FormData = {
                    objective:
                        optimizeOptions.find((o: OptimizeOption) => o.value === base_input.objective)?.label ||
                        base_input.objective,
                    start_date: base_input.start_date,
                    max_promo_weeks: base_input.max_promo_weeks,
                    keep_top: base_input.keep_top,
                    no_of_concurrent_tprs: base_input.concurrent_tprs,
                    no_of_concurrent_fds: base_input.concurrent_fds,
                    no_of_concurrent_mdfs: base_input.concurrent_mdfs,
                };
                const scopeData = {
                    brands: base_input.brands_list,
                    sub_brands: base_input.sub_brands_list,
                };
                tableData.sort((a, b) => Number(b.objectiveConstraint) - Number(a.objectiveConstraint));
                return { formData, tableData, scopeData, level };
            } catch (error) {
                setOpenOptimizeResultModal(false);
                throw error;
            }
        },
    });

    const parsedData = useMemo(() => {
        if (data) {
            return {
                formData: data.formData as FormData,
                tableData: data.tableData as OptimisationPlanData[],
                scopeData: data.scopeData,
                level: data.level,
            };
        }
        return { formData: {} as FormData, tableData: [] };
    }, [data]);

    const getParsedTableData = (level: Level, data: IApiConstraintDetails[]): OptimisationPlanData[] => {
        if (level === 'category') {
            return data.map((item) => {
                const differenceValue =
                    Number(item.level_details[0].details.optimized_value) -
                    Number(item.level_details[0].details.original_value);
                return {
                    label: item.label,
                    name: item.constraint_name ?? '',
                    type: InputTypes.Range,
                    min_value: item.level_details[0].details.min ?? null,
                    max_value: item.level_details[0].details.max ?? null,
                    value: item.level_details[0].details.original_value,
                    optimized: item.level_details[0].details.optimized_value,
                    originalValue: item.level_details[0].details.original_value,
                    variance: differenceValue,
                    default_value: item.level_details[0].details.default_value,
                    objectiveConstraint: item.objective_constraint,
                };
            });
        } else {
            return data.map((item) => {
                return {
                    label: item.label,
                    name: item.constraint_name ?? '',
                    type: InputTypes.Range,
                    min_value: null,
                    max_value: null,
                    value: null,
                    originalValue: 0,
                    default_value: null,
                    objectiveConstraint: item.objective_constraint,
                    levelDetails: item.level_details.map((levelDetail) => {
                        const differenceValue =
                            Number(levelDetail.details.optimized_value) - Number(levelDetail.details.original_value);
                        return {
                            label: levelDetail.attribute_name,
                            name: levelDetail.attribute_name,
                            type: InputTypes.Range,
                            value: levelDetail.details.original_value,
                            optimized: levelDetail.details.optimized_value,
                            originalValue: levelDetail.details.original_value,
                            variance: differenceValue,
                            default_value: levelDetail.details.default_value,
                            min_value: levelDetail.details.min ?? null,
                            max_value: levelDetail.details.max ?? null,
                        };
                    }),
                };
            });
        }
        return [];
    };

    return (
        <div>
            <CustomModal
                maskClosable={false}
                destroyOnClose
                centered
                open={open}
                title="Optimization Result"
                footer={null}
                onCancel={() => setOpenOptimizeResultModal(false)}
                style={{
                    borderRadius: 8,
                    height: `calc(${percentageHeight}px)`,
                }}
                bodyStyle={{ height: `calc(${percentageHeight}px - 134px)` }}
                width={percentageWidth}
            >
                {loading ? (
                    <div className=" h-[448px] border border-backgroundGreyAlt6  bg-backgroundGreyAlt6  rounded p-3   flex justify-center items-center  ">
                        <Spin />
                    </div>
                ) : (
                    data && (
                        <div className="h-full flex flex-col gap-3 overflow-hidden">
                            <OptimizePlanForm formData={parsedData.formData} viewMode={true} />
                            <ScopeOfOptimization
                                selectedBrands={parsedData.scopeData.brands}
                                setSelectedBrands={() => {}}
                                selectedSubBrands={parsedData.scopeData.sub_brands}
                                setSelectedSubBrands={() => {}}
                                attributeFilters={[]}
                                attributeFiltersLoading={false}
                                setLevel={() => {}}
                                viewMode={true}
                            />
                            <div className="border border-[rgba(240,240,240,1)] rounded h-full flex flex-col min-h-0">
                                <div className="sticky top-0 bg-white z-10">
                                    <OptimizePlanTableHeader headerColumns={tableHeaders} level={parsedData.level} />
                                </div>

                                <div className="overflow-y-auto flex-1 min-h-0">
                                    {parsedData.tableData.map((item, index) => (
                                        <OptimizeTableContent
                                            key={item.name}
                                            rowData={item}
                                            index={index}
                                            level={parsedData.level}
                                            tableColumns={tableColumns}
                                            expandRows={true}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )
                )}
            </CustomModal>
        </div>
    );
};

export default OptimizeResultModal;
